import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { RefreshCcw, Users, Phone, Mail, Briefcase, Search, CheckCircle2, Clock } from 'lucide-react';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const isSameDay = (value, dateFilter) => {
  if (!value || !dateFilter) return true;
  try {
    const itemDate = new Date(value);
    const filterDate = new Date(`${dateFilter}T00:00:00`);
    return itemDate.toDateString() === filterDate.toDateString();
  } catch {
    return true;
  }
};

export default function DemoRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [rubroFilter, setRubroFilter] = useState('todos');
  const [dateFilter, setDateFilter] = useState('');

  const isMasterAdmin = user?.email?.toLowerCase() === 'adpenia@gmail.com';

  const normalizedRequests = useMemo(
    () => requests.map((item) => ({ ...item, estado: item.estado || 'pendiente' })),
    [requests]
  );

  const rubroOptions = useMemo(
    () => ['todos', ...new Set(normalizedRequests.map((item) => item.rubro).filter(Boolean))],
    [normalizedRequests]
  );

  const filteredRequests = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return normalizedRequests.filter((item) => {
      const matchesSearch = !query || [item.nombre, item.email, item.nombre_negocio, item.telefono]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));

      const matchesStatus = statusFilter === 'todos' || item.estado === statusFilter;
      const matchesRubro = rubroFilter === 'todos' || item.rubro === rubroFilter;
      const matchesDate = isSameDay(item.created_at, dateFilter);

      return matchesSearch && matchesStatus && matchesRubro && matchesDate;
    });
  }, [normalizedRequests, searchTerm, statusFilter, rubroFilter, dateFilter]);

  const stats = useMemo(() => ({
    total: normalizedRequests.length,
    today: normalizedRequests.filter((item) => {
      if (!item.created_at) return false;
      return new Date(item.created_at).toDateString() === new Date().toDateString();
    }).length,
    pending: normalizedRequests.filter((item) => item.estado === 'pendiente').length,
    contacted: normalizedRequests.filter((item) => item.estado === 'contactado').length,
  }), [normalizedRequests]);

  const loadRequests = async () => {
    if (!isMasterAdmin) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('demo_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar las solicitudes de demo');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (item) => {
    const nextStatus = (item.estado || 'pendiente') === 'pendiente' ? 'contactado' : 'pendiente';
    try {
      const { error } = await supabase
        .from('demo_requests')
        .update({ estado: nextStatus })
        .eq('id', item.id);

      if (error) throw error;

      toast.success(`Marcado como ${nextStatus}`);
      setRequests((prev) => prev.map((row) => (
        row.id === item.id ? { ...row, estado: nextStatus } : row
      )));
    } catch (error) {
      console.error(error);
      toast.error('No se pudo actualizar el estado');
    }
  };

  useEffect(() => {
    if (!isMasterAdmin) {
      navigate('/sistema/dashboard', { replace: true });
      return;
    }
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterAdmin]);

  if (!isMasterAdmin) return null;

  return (
    <div className="imp-page imp-container animate-nova-in px-3 py-3 sm:px-4 sm:py-4 lg:px-5">
      <div className="imp-header mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[var(--surface)] border border-[color:var(--border)] rounded-md flex items-center justify-center shadow-sm shrink-0">
            <Users size={18} className="text-[#10B981]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-[var(--color-accent-dark)] tracking-tight uppercase truncate">Solicitudes Demo</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">
              Leads comerciales recibidos desde la web
            </p>
          </div>
        </div>

        <div className="imp-header-actions w-full md:w-auto">
          <button
            onClick={loadRequests}
            disabled={loading}
            className="w-full md:w-auto px-4 py-2.5 rounded-md bg-[var(--surface)] border border-[color:var(--border)] text-[var(--text-default)] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCcw size={16} />
            Recargar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] mb-1">Total</p>
          <p className="text-3xl font-black text-[var(--text-default)]">{stats.total}</p>
        </div>
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] mb-1">Hoy</p>
          <p className="text-3xl font-black text-[var(--text-default)]">{stats.today}</p>
        </div>
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">Pendientes</p>
          <p className="text-3xl font-black text-[var(--text-default)]">{stats.pending}</p>
        </div>
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-[var(--color-accent-dark)] mb-1">Contactados</p>
          <p className="text-3xl font-black text-[var(--text-default)]">{stats.contacted}</p>
        </div>
      </div>

      <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] overflow-hidden shadow-sm">
        <div className="px-4 py-3.5 sm:px-5 border-b border-slate-100 bg-[var(--surface-2)] flex items-center gap-2">
          <Briefcase size={16} className="text-[var(--text-muted)]" />
          <span className="font-black text-[var(--color-accent-dark)] text-[11px] uppercase tracking-widest">Listado</span>
        </div>

        <div className="p-4 border-b border-slate-100 bg-[var(--surface)]">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 w-full bg-[var(--surface-2)] border border-[color:var(--border)] rounded-md px-3 py-2">
              <Search size={16} className="text-[var(--text-muted)]" />
              <input
                className="bg-transparent outline-none w-full text-sm font-medium text-[var(--text-default)] placeholder:text-[var(--text-muted)]"
                placeholder="Buscar por nombre, email o negocio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full bg-[var(--surface)] border border-[color:var(--border)] px-3 py-2 rounded-md text-sm font-medium text-[var(--text-default)] outline-none"
            >
              <option value="todos">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="contactado">Contactado</option>
            </select>

            <select
              value={rubroFilter}
              onChange={(e) => setRubroFilter(e.target.value)}
              className="w-full bg-[var(--surface)] border border-[color:var(--border)] px-3 py-2 rounded-md text-sm font-medium text-[var(--text-default)] outline-none"
            >
              {rubroOptions.map((option) => (
                <option key={option} value={option}>
                  {option === 'todos' ? 'Todos los rubros' : option}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full bg-[var(--surface)] border border-[color:var(--border)] px-3 py-2 rounded-md text-sm font-medium text-[var(--text-default)] outline-none"
            />
          </div>

          <div className="mt-3 text-xs text-[var(--text-muted)] font-medium">
            {filteredRequests.length} resultados
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[var(--text-muted)] text-sm font-bold">Cargando solicitudes...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)] text-sm font-bold">No hay solicitudes que coincidan con esos filtros.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredRequests.map((item) => (
              <div key={item.id || `${item.email}-${item.created_at}`} className="p-4 sm:p-5">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-sm font-black text-[var(--text-default)]">{item.nombre || 'Sin nombre'}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)]">{formatDateTime(item.created_at)}</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 text-sm text-[var(--text-muted)]">
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail size={14} className="shrink-0 text-[var(--text-muted)]" />
                        <span className="truncate">{item.email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <Phone size={14} className="shrink-0 text-[var(--text-muted)]" />
                        <span className="truncate">{item.telefono || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <Briefcase size={14} className="shrink-0 text-[var(--text-muted)]" />
                        <span className="truncate">{item.nombre_negocio || 'Sin negocio'}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <Users size={14} className="shrink-0 text-[var(--text-muted)]" />
                        <span className="truncate">{item.rubro || 'Sin rubro'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 justify-between xl:justify-end">
                    {item.estado === 'pendiente' ? (
                      <span className="flex items-center gap-1.5 bg-amber-50 text-amber-600 px-2.5 py-1 rounded-full text-xs font-bold border border-amber-200">
                        <Clock size={12} strokeWidth={3} />
                        Pendiente
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] px-2.5 py-1 rounded-full text-xs font-bold border border-[color:var(--color-accent)]">
                        <CheckCircle2 size={12} strokeWidth={3} />
                        Contactado
                      </span>
                    )}

                    <button
                      onClick={() => toggleStatus(item)}
                      className={`px-3 py-2 rounded-md text-[11px] font-black uppercase tracking-widest transition-all ${
                        item.estado === 'pendiente'
                          ? 'bg-[#E0F2FE] text-[#0284C7] hover:bg-[#BAE6FD]'
                          : 'bg-[var(--surface-2)] text-[var(--text-muted)] hover:bg-slate-200'
                      }`}
                    >
                      {item.estado === 'pendiente' ? 'Marcar contactado' : 'Marcar pendiente'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
